/**
 * Sourcing — the recruiter's side of the sourcing agent (Sofia, Oct 16 2026: everything steered from
 * People Space). Three parts:
 *
 *  - JOBS: how many people to find, what to focus on, what to leave out, and a cap on profiles
 *    opened. The agent (a Claude skill in the recruiter's own Chrome) picks the oldest open job up,
 *    works it in LinkedIn Recruiter and reports progress here.
 *  - SHORTLIST: every person whose FULL profile was read and checked (Oct 23 2026: people the run
 *    has not read yet wait under "Not read yet", where Accept is replaced by "Read profile"),
 *    with one evidence line per must-have. The recruiter decides:
 *    Accept (onto the Candidates list — the extension then reads the profile so the check can quote
 *    it), Hold, or Reject with a reason. Never ranked, newest first (EU AI Act Annex III, as on the
 *    Candidates tab).
 *  - NOT A FIT, IN THE AGENT'S VIEW: shown, collapsed, with the agent's reason and the same buttons —
 *    nobody is filtered out where the recruiter cannot see it. Grouped by the reason (Oct 23 2026,
 *    `MisfitList`).
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { logAnalyticsEvent } from '../../../../analytics/nativeAnalytics';
import * as api from '../../services/taOutreachApi';
import type { JobCost, OutreachCandidate, SourcingJob } from '../../types/taOutreach';
import { Badge, Card } from './outreachUi';
import { ownerFirstName } from './ownerName';
import { canSource, extensionVersion } from './outreachExtension';
import { ExtensionSetup } from './ExtensionSetup';
import { useSourcingRun } from './useSourcingRun';
import { MustHaveFilter, mustHaveLabels, shownLabels } from './MustHaveFilter';
import { recruiterProfileUrl } from './profileLink';
import { ShortlistCard } from './ShortlistCard';
import { UnreadList } from './UnreadList';
import { JobForm } from './SourcingJobForm';
import { RunStatus } from './RunStatus';
import { DroppedList } from './DroppedList';
import { SHORTLIST_PAGE, ShortlistBar } from './ShortlistBar';
import { downloadFile, qualityCsv, qualityFileName } from './qualityCheck';
import { MisfitList } from './MisfitList';

const fmt = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' }) : '';

const OUTCOME: Record<NonNullable<SourcingJob['checked']>[number]['outcome'], { label: string; tone: 'green' | 'amber' | 'grey' }> = {
  shortlist: { label: 'Shortlist', tone: 'green' },
  extra: { label: 'Fits, not added', tone: 'grey' },
  not_fit: { label: 'Not a fit', tone: 'amber' },
  contacted: { label: 'Contacted', tone: 'grey' },
  blocked: { label: 'Skipped', tone: 'grey' },
  known: { label: 'Already listed', tone: 'grey' },
};

/** Must-haves the candidate shows: from the full-profile check when there is one, else from the list card. */
function greens(c: OutreachCandidate): number {
  if (c.gap) return c.gap.criteria.filter((x) => x.mustHave && x.verdict === 'evidenced').length;
  return (c.sourcing?.evidence || []).filter((e) => e.verdict === 'yes').length;
}

/** Must-haves the candidate's profile contradicts. */
function reds(c: OutreachCandidate): number {
  if (c.gap) return c.gap.criteria.filter((x) => x.mustHave && x.verdict === 'contradicted').length;
  return (c.sourcing?.evidence || []).filter((e) => e.verdict === 'no').length;
}

const STEP_LABELS: Record<string, string> = {
  screening: 'screening',
  profile_check: 'profile checks',
  draft: 'drafts',
  rewrite: 'rewrites',
  web_facts: 'web facts',
  sample_draft: 'sample drafts',
};
const usd = (v: number) => `$${v.toFixed(2)}`;

/** " · AI cost $1.84" with the split by step on hover (Oct 26 2026). */
export function JobCostText({ cost }: { cost: JobCost }) {
  const split = Object.entries(cost.byStep)
    .sort((a, b) => b[1] - a[1])
    .map(([k, v]) => `${STEP_LABELS[k] || k} ${usd(v)}`)
    .join(' · ');
  return (
    <span title={`${split}${cost.unpriced ? ` · ${cost.unpriced} call(s) on a model without a price, not counted` : ''}`}>
      {' '}· AI cost {usd(cost.usd)}
    </span>
  );
}

/** Download the quality check of one run (everyone looked at, verdicts, quotes, card text). */
async function downloadQuality(job: SourcingJob, ext: 'csv' | 'json') {
  try {
    const data = await api.fetchSourcingExport(job.id);
    downloadFile(qualityFileName(data, ext), ext === 'csv' ? qualityCsv(data) : JSON.stringify(data, null, 2), ext === 'csv' ? 'text/csv;charset=utf-8' : 'application/json');
    void logAnalyticsEvent('ta_outreach_quality_check_downloaded', { format: ext, rows: data.rows.length });
  } catch (err) {
    showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not build the quality check.' });
  }
}

/** Everyone the run looked at, by name, with what happened and why (Sofia, Oct 18 2026). */
function CheckedList({ checked }: { checked: NonNullable<SourcingJob['checked']> }) {
  return (
    <details className="w-full">
      <summary className="cursor-pointer text-xs font-semibold text-[#1A1A1A]">Who was checked ({checked.length})</summary>
      <ol className="mt-1 max-h-72 space-y-0.5 overflow-auto pl-5 text-xs">
        {checked.map((c) => (
          <li key={c.recruiterId}>
            <a href={recruiterProfileUrl(c.recruiterId)} target="_blank" rel="noreferrer" className="text-[#1A1A1A] underline">
              {c.name}
            </a>{' '}
            <Badge tone={OUTCOME[c.outcome].tone}>{OUTCOME[c.outcome].label}</Badge>
            {c.reason && <span className="text-[#707070]"> · {c.reason}</span>}
          </li>
        ))}
      </ol>
    </details>
  );
}

function JobRow({
  job,
  onCancelled,
  onRerun,
  run,
  canRun,
}: {
  job: SourcingJob;
  onCancelled: (j: SourcingJob) => void;
  /** A finished or stopped job: the same search and settings again, as a new job. */
  onRerun: (job: SourcingJob) => void;
  run: ReturnType<typeof useSourcingRun>;
  canRun: boolean;
}) {
  const p = job.progress;
  const live = job.status === 'open' || job.status === 'running';
  const runningThis = run.runningId === job.id;
  const status = run.status?.jobId === job.id ? run.status : null;
  return (
    <li className="flex flex-wrap items-baseline gap-2 border-b border-solid border-x-0 border-t-0 border-[#E9EAEC] py-2 last:border-b-0">
      {/* The list part can be done (target reached) while the full profiles are still being read. */}
      {runningThis ? (
        <Badge tone="blue">running</Badge>
      ) : (
        <Badge tone={job.status === 'running' ? 'blue' : job.status === 'done' ? 'green' : job.status === 'cancelled' ? 'grey' : 'amber'}>{job.status}</Badge>
      )}
      <span>
        {job.sourceUrl ? (job.sourceUrl.includes('/discover/') ? 'Recruiter search · ' : 'Project Pipeline · ') : ''}
        {job.target} candidate{job.target === 1 ? '' : 's'}
        {job.focus ? ` · focus: ${job.focus}` : ''}
        {job.exclude ? ` · without: ${job.exclude}` : ''}
      </span>
      <span className="text-xs text-[#707070]">
        {p.reviewed} checked · {p.saved} on the shortlist · {p.skippedFit} not a fit · {p.skippedPipeline} skipped (contacted, in a FINN process or already listed) ·{' '}
        {job.sourceUrl ? `checks at most ${job.maxScreened ?? 250}` : `max ${job.maxProfiles} profiles`}
        {p.note ? ` · “${p.note}”` : ''}{p.leverNote ? ` · ⚠ ${p.leverNote}` : ''} · created {fmt(job.createdAt)}
        {/* Every recruiter's jobs for the role are listed (Oct 27 2026): say whose. */}
        {job.owner ? ` by ${ownerFirstName(job.owner)}` : ''}
        {job.cost && job.cost.calls > 0 && <JobCostText cost={job.cost} />}
      </span>
      {status && <RunStatus key={status.since} status={status} running={runningThis} />}
      {/* All actions in one row, the fold-outs below it (Sofia, Oct 19 2026). */}
      <span className="flex w-full flex-wrap items-center gap-2">
        {live && job.sourceUrl && !runningThis && (
          <Button
            variant="primary"
            size="sm"
            className="rounded-md"
            disabled={!canRun || Boolean(run.runningId)}
            title={!canRun ? 'Install or update the FINN Outreach Sender — see the yellow box at the top of this step' : undefined}
            onClick={() => void run.start(job)}
          >
            {p.reviewed ? 'Continue sourcing' : 'Start sourcing'}
          </Button>
        )}
        {runningThis && (
          <Button variant="outline" size="sm" className="rounded-md" onClick={run.stop}>
            Stop
          </Button>
        )}
        {!live && job.sourceUrl && !run.runningId && (
          <Button variant="outline" size="sm" className="rounded-md" onClick={() => onRerun(job)}>
            Run again
          </Button>
        )}
        {(job.checked?.length ?? 0) > 0 && (
          <Button variant="outline" size="sm" className="rounded-md" onClick={() => void downloadQuality(job, 'csv')}>
            Download quality check (CSV)
          </Button>
        )}
        {live && !runningThis && (
          <Button
            variant="ghost"
            size="sm"
            className="rounded-md text-[#707070]"
            onClick={() =>
              void api
                .cancelSourcingJob(job.id)
                .then((r) => onCancelled(r.job))
                .catch((err) => showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not stop the job.' }))
            }
          >
            Stop
          </Button>
        )}
      </span>
      {(job.checked?.length ?? 0) > 0 && <CheckedList checked={job.checked || []} />}
    </li>
  );
}

export function SourcingPanel({
  postingId,
  roleTitle,
  shortlisted,
  rejected = [],
  onRestore,
  restoring = null,
  onUpdate,
  onAccepted,
  onAdded,
}: {
  postingId: string;
  roleTitle: string;
  shortlisted: OutreachCandidate[];
  /** Rejected from this shortlist — listed below it, with "Back to the shortlist" (Oct 19 2026). */
  rejected?: OutreachCandidate[];
  onRestore?: (c: OutreachCandidate) => void;
  restoring?: string | null;
  onUpdate: (item: OutreachCandidate) => void;
  /** New shortlist entries from a sourcing run. */
  onAdded: (items: OutreachCandidate[]) => void;
  /**
   * After Accept, everyone accepted in that click, at once (Nov 2 2026). A hand-off: the view
   * drafts the queued ones and reads the unread ones in the background, never while this waits.
   */
  onAccepted: (items: OutreachCandidate[]) => void;
}) {
  const [jobs, setJobs] = useState<SourcingJob[]>([]);
  /** Shortlist filter (Sofia, Oct 19 2026): only people whose profile shows EVERY ticked must-have. */
  const [mustShow, setMustShow] = useState<string[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [version, setVersion] = useState<string | null | undefined>(undefined), canRun = canSource(version);
  const run = useSourcingRun({ onJob: (j) => setJobs((list) => list.map((x) => (x.id === j.id ? j : x))), onItems: onAdded });
  useEffect(() => {
    let alive = true;
    void extensionVersion().then((v) => alive && setVersion(v));
    return () => {
      alive = false;
    };
  }, []);
  const loadJobs = useCallback(async () => {
    try {
      setJobs((await api.fetchSourcingJobs(postingId)).jobs);
    } catch {
      /* the toast from the API layer is enough */
    }
  }, [postingId]);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- fetching on mount is the external sync
    void loadJobs();
    const t = setInterval(() => void loadJobs(), 60_000);
    return () => clearInterval(t);
  }, [loadJobs]);

  /**
   * Accept = one request for everyone ticked (Sofia, Nov 2 2026: "takes forever" — it accepted,
   * queued and drafted one person after the other, ~25 s each): the proxy accepts and queues them
   * all (`acceptMany`), they are in Write & send right away, and the drafts are written there by
   * themselves. A refusal for one is named; the others go through.
   */
  async function accept(ids: string[]) {
    const { results } = await api.acceptMany(ids);
    const done: OutreachCandidate[] = [];
    const problems: string[] = [];
    for (const r of results) {
      if (r.item) {
        onUpdate(r.item);
        done.push(r.item);
        if (r.queueError) problems.push(r.queueError.error);
      } else {
        problems.push(`${shortlisted.find((c) => c.id === r.id)?.name || 'One of them'}: ${r.error}`);
      }
    }
    if (problems.length) showToast({ status: 'error', position: 'bottomCenter', description: problems.join(' · ') });
    if (done.length) onAccepted(done);
    return done;
  }

  async function decide(c: OutreachCandidate, decision: 'accept' | 'reject', reason?: string) {
    setBusy(c.id);
    try {
      if (decision === 'accept') await accept([c.id]);
      else onUpdate((await api.decideShortlisted(c.id, decision, reason)).item);
      void logAnalyticsEvent('ta_outreach_shortlist_decided', { decision, agent_said_no_fit: Boolean(c.sourcing?.skipReason), read: Boolean(c.gap) });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not save that.' });
    } finally {
      setBusy(null);
    }
  }

  /**
   * Accept everyone ticked, in one request (Nov 2 2026). Everyone on the page starts ticked (Sofia,
   * Oct 27 2026: "per default sollen alle selected sein"), so what is kept is who was UNticked.
   */
  const [unticked, setUnticked] = useState<Set<string>>(new Set());
  const tick = (ids: string[], on: boolean) => setUnticked((cur) => new Set(on ? [...cur].filter((x) => !ids.includes(x)) : [...cur, ...ids]));
  const [accepting, setAccepting] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  async function acceptSelected(ids: string[]) {
    setAccepting(`${ids.length}`);
    try {
      await accept(ids);
      void logAnalyticsEvent('ta_outreach_shortlist_bulk_accepted', { count: ids.length });
    } catch (err) {
      showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not accept them.' });
    } finally {
      setAccepting(null);
    }
  }

  // Most must-haves shown first (Sofia, Oct 18 2026: "am meisten grün oben"), then fewest
  // contradicted, then newest. The full-profile check counts once it exists, else the list evidence.
  const newest = [...shortlisted].sort(
    (a, b) => greens(b) - greens(a) || reds(a) - reds(b) || b.createdAt.localeCompare(a.createdAt)
  );
  const fitsAll = newest.filter((c) => !c.sourcing?.skipReason);
  // On the shortlist only once the full profile was read and checked (Sofia, Oct 23 2026).
  const allFits = fitsAll.filter((c) => c.gap);
  const unread = fitsAll.filter((c) => !c.gap);
  const labels = mustHaveLabels(allFits);
  const fits = allFits.filter((c) => mustShow.every((l) => shownLabels(c).has(l)));
  const pages = Math.max(1, Math.ceil(fits.length / SHORTLIST_PAGE));
  const at = Math.min(page, pages - 1);
  const pageFits = fits.slice(at * SHORTLIST_PAGE, (at + 1) * SHORTLIST_PAGE);
  const ticked = pageFits.filter((c) => !unticked.has(c.id)).map((c) => c.id);
  // ONE job in view — the one running in this tab, else the newest; every other job folds into
  // "Earlier jobs" (Sofia, Oct 19 2026). A job left "running" by a closed tab is not "in view".
  const shownJob = jobs.find((j) => j.id === run.runningId) || jobs[0] || null;
  const olderJobs = jobs.filter((j) => j !== shownJob);
  const jobRow = (j: SourcingJob) => (
    <JobRow
      key={j.id}
      job={j}
      run={run}
      canRun={canRun}
      onCancelled={(n) => setJobs((list) => list.map((x) => (x.id === n.id ? n : x)))}
      onRerun={(old) =>
        void api
          .createSourcingJob({
            postingId,
            roleTitle,
            target: old.target,
            focus: old.focus || '',
            exclude: old.exclude || '',
            maxProfiles: old.maxProfiles,
            sourceUrl: old.sourceUrl || '',
            maxScreened: old.maxScreened,
          })
          .then((r) => setJobs((list) => [r.job, ...list]))
          .catch((err) => showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'Could not create the job.' }))
      }
    />
  );
  const misfits = newest.filter((c) => c.sourcing?.skipReason);
  return (
    <div className="space-y-4 text-[13px]">
      <ExtensionSetup version={version} />
      <Card className="space-y-3">
        <h3 className="m-0 text-base font-semibold">
          Sourcing jobs {jobs.length > 0 && <span className="text-xs font-normal text-[#979797]">{jobs.length}</span>}
        </h3>
        <details>
          <summary className="cursor-pointer text-xs font-semibold text-[#1A1A1A]">How sourcing works</summary>
          <p className="m-0 mt-1 text-[#707070]">
          Run your search in LinkedIn Recruiter (inside the role’s project), paste its address here and click “Start sourcing”. The FINN
          Outreach Sender reads the results page by page in the next tab, People Space checks each person against the brief, and only the
          fits get their full profile read and checked — they land on the shortlist below. Nothing is sent in LinkedIn; the only clicks save fits to the project’s pipeline and open the profile’s own “show more” sections. Anyone
          FINN contacted in the last six months, anyone already in a FINN process and former FINN employees are skipped. It stops at the
          first LinkedIn warning. A project link without a search reads the project’s Pipeline. A new job on a list you already read
            for this role continues where the last one stopped.
          </p>
        </details>
        <JobForm
          postingId={postingId}
          roleTitle={roleTitle}
          canRun={canRun}
          running={Boolean(run.runningId)}
          savedUrl={jobs.find((j) => j.sourceUrl)?.sourceUrl || null}
          onCreated={(j) => {
            setJobs((list) => [j, ...list]);
            void run.start(j);
          }}
        />
        {shownJob && <ul className="m-0 list-none p-0">{jobRow(shownJob)}</ul>}
        {olderJobs.length > 0 && (
          <details>
            <summary className="cursor-pointer text-xs font-semibold text-[#1A1A1A]">Earlier jobs ({olderJobs.length})</summary>
            <ul className="m-0 mt-1 list-none p-0">{olderJobs.map(jobRow)}</ul>
          </details>
        )}
      </Card>

      <h3 className="m-0 text-base font-semibold">
        Shortlist{' '}
        <span className="text-xs font-normal text-[#979797]">
          {mustShow.length ? `${fits.length} of ${allFits.length}` : fits.length} · most must-haves shown first
        </span>
      </h3>
      <MustHaveFilter labels={labels} candidates={allFits} value={mustShow} onChange={setMustShow} />
      <ShortlistBar
        pageIds={pageFits.map((c) => c.id)}
        selected={new Set(ticked)}
        onSelect={tick}
        onAccept={() => void acceptSelected(ticked)}
        accepting={accepting}
        page={at}
        pages={pages}
        onPage={(p) => {
          setPage(p);
          window.scrollTo?.({ top: 0, behavior: 'smooth' });
        }}
      />
      {pageFits.map((c) => (
        <ShortlistCard
          key={c.id}
          c={c}
          busy={busy === c.id || Boolean(accepting)}
          onDecide={(d, r) => void decide(c, d, r)}
          selected={!unticked.has(c.id)}
          onSelect={(on) => tick([c.id], on)}
        />
      ))}
      {!fits.length && (
        <p className="text-[#707070]">
          {mustShow.length
            ? 'Nobody on the shortlist shows all of these.'
            : unread.length
              ? 'Nobody checked yet: the people below wait for their full profile to be read.'
              : 'Nobody waiting. Create a job above and click “Start sourcing” — the fits show up here.'}
        </p>
      )}

      {unread.length > 0 && (
        <UnreadList people={unread} run={run} canRun={canRun} busy={busy} onDecide={(c, d, r) => void decide(c, d, r)} />
      )}

      <MisfitList misfits={misfits} renderCard={(c) => <ShortlistCard key={c.id} c={c} busy={busy === c.id} onDecide={(d, r) => void decide(c, d, r)} />} />

      {onRestore && <DroppedList title="Rejected" people={rejected} restoring={restoring} onRestore={onRestore} restoreLabel="Back to the shortlist" />}

    </div>
  );
}
